import { Button } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import {
  SubscriptionService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import {
  SubscriptionPlan,
  SubscriptionRecurring,
  SubscriptionStatus,
} from '@affine/graphql';
import { type I18nString, i18nTime, Trans, useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { RedeemCode } from '../plans/plan-card';
import { CardNameLabelRow } from './card-name-label-row';
import { PaymentMethodUpdater } from './payment-method';
import * as styles from './style.css';

const DescriptionI18NKey = {
  Basic: 'com.affine.payment.billing-setting.current-plan.description',
  Monthly:
    'com.affine.payment.billing-setting.current-plan.description.monthly',
  Yearly: 'com.affine.payment.billing-setting.current-plan.description.yearly',
  Lifetime:
    'com.affine.payment.billing-setting.current-plan.description.lifetime',
} as const satisfies { [key: string]: I18nString };

const getMessageKey = (
  plan: SubscriptionPlan,
  recurring: SubscriptionRecurring
) => {
  if (plan !== SubscriptionPlan.Pro && plan !== SubscriptionPlan.Team) {
    return DescriptionI18NKey.Basic;
  }
  return DescriptionI18NKey[recurring];
};

// Map internal enum plan name to user-facing Curiescious tier label.
const PLAN_DISPLAY_NAME: Partial<Record<SubscriptionPlan, string>> = {
  [SubscriptionPlan.Pro]: 'Researcher',
  [SubscriptionPlan.Team]: 'Lab',
  [SubscriptionPlan.AI]: 'Curiescious AI',
};

function planDisplayName(plan: SubscriptionPlan): string {
  return PLAN_DISPLAY_NAME[plan] ?? plan;
}

export const ProPlanCard = ({
  gotoCloudPlansSetting,
}: {
  gotoCloudPlansSetting: () => void;
}) => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    subscriptionService.prices.revalidate();
    workspaceSubscriptionService.subscription.revalidate();
  }, [subscriptionService, workspaceSubscriptionService]);
  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const workspaceSub = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );

  const proPrice = useLiveData(subscriptionService.prices.proPrice$);
  const teamPrice = useLiveData(subscriptionService.prices.teamPrice$);
  const allPrices = useLiveData(subscriptionService.prices.prices$);

  // Display whichever active subscription exists. Workspace-level Lab/Team
  // outranks user-level Researcher since Lab is the higher tier.
  const activeSubscription = workspaceSub ?? proSubscription;
  const currentPlan = activeSubscription?.plan ?? SubscriptionPlan.Free;
  const currentRecurring =
    activeSubscription?.recurring ?? SubscriptionRecurring.Monthly;

  // Pick the price tier matching the active plan. Lab uses team pricing,
  // Researcher uses pro pricing.
  const planPrice =
    currentPlan === SubscriptionPlan.Team
      ? (teamPrice ??
        allPrices?.find(p => p.plan === SubscriptionPlan.Team) ??
        null)
      : proPrice;

  const amount = activeSubscription
    ? planPrice
      ? activeSubscription.recurring === SubscriptionRecurring.Monthly
        ? String((planPrice.amount ?? 0) / 100)
        : String((planPrice.yearlyAmount ?? 0) / 100)
      : '?'
    : '0';

  return (
    <div className={styles.planCard}>
      <div className={styles.currentPlan}>
        <SettingRow
          spreadCol={false}
          name={
            <CardNameLabelRow
              cardName={t['com.affine.payment.billing-setting.current-plan']()}
              status={activeSubscription?.status}
            />
          }
          desc={
            <>
              <Trans
                i18nKey={getMessageKey(currentPlan, currentRecurring)}
                values={{
                  planName: planDisplayName(currentPlan),
                }}
                components={{
                  1: (
                    <span
                      onClick={gotoCloudPlansSetting}
                      className={styles.currentPlanName}
                    />
                  ),
                }}
              />
              <CloudExpirationInfo />
            </>
          }
        />
        <PlanAction
          plan={currentPlan}
          subscriptionStatus={activeSubscription?.status}
          gotoPlansSetting={gotoCloudPlansSetting}
        />
      </div>
      <p className={styles.planPrice}>
        ${amount}
        <span className={styles.billingFrequency}>
          /
          {currentRecurring === SubscriptionRecurring.Monthly
            ? t['com.affine.payment.billing-setting.month']()
            : t['com.affine.payment.billing-setting.year']()}
        </span>
      </p>
    </div>
  );
};

const CloudExpirationInfo = () => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const workspaceSub = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );
  const subscription = workspaceSub ?? proSubscription;

  let text = '';

  if (subscription?.status === SubscriptionStatus.PastDue) {
    text = t['com.affine.payment.billing-tip.past-due']({
      due: i18nTime(subscription.nextBillAt, {
        absolute: { accuracy: 'day' },
      }),
    });
  } else if (subscription?.nextBillAt) {
    text = t['com.affine.payment.billing-setting.renew-date.description']({
      renewDate: i18nTime(subscription.nextBillAt, {
        absolute: { accuracy: 'day' },
      }),
    });
  } else if (subscription?.end) {
    text = t['com.affine.payment.billing-setting.due-date.description']({
      dueDate: i18nTime(subscription.end, {
        absolute: { accuracy: 'day' },
      }),
    });
  }

  return text ? (
    <>
      <br />
      {text}
    </>
  ) : null;
};

const PlanAction = ({
  plan,
  subscriptionStatus,
  gotoPlansSetting,
}: {
  plan: string;
  gotoPlansSetting: () => void;
  subscriptionStatus?: SubscriptionStatus;
}) => {
  const t = useI18n();

  const subscription = useService(SubscriptionService).subscription;
  const isOnetimePro = useLiveData(subscription.isOnetimePro$);

  if (isOnetimePro) {
    return <RedeemCode variant="primary" className={styles.planAction} />;
  }

  // On any paid plan (Pro/Researcher or Team/Lab) the action is "Change plan",
  // not "Upgrade". Free users see "Upgrade".
  const isPaidPlan =
    plan === SubscriptionPlan.Pro || plan === SubscriptionPlan.Team;

  return (
    <>
      <Button
        className={styles.planAction}
        variant="primary"
        onClick={gotoPlansSetting}
      >
        {isPaidPlan
          ? t['com.affine.payment.billing-setting.change-plan']()
          : t['com.affine.payment.billing-setting.upgrade']()}
      </Button>
      {subscriptionStatus === SubscriptionStatus.PastDue ? (
        <PaymentMethodUpdater
          inCardView
          className={styles.manageMentInCard}
          variant="primary"
        />
      ) : null}
    </>
  );
};
